/**
 * QuickSearchTrigger - the quick-search entry in the CRM's blue top bar
 * (docs/plans/crm-report-builder-search-collab-plan.md A2.2, A2.3 step 7, B2.5; decision Q6 a; WP Q-4).
 *
 * From sm: up it is a compact box, "Search leads" plus the shortcut hint ("Ctrl K", or "Cmd K" on a
 * Mac; the hint shows from md: up, where the bar has room for it beside the tool switcher, the theme
 * toggle and the Tool Hub link). Below sm: it is a 44 px search icon that opens the same palette full
 * screen. One button either way, so it sits in the tab order once.
 *
 * X-4 places it at the start of the header's right group (CRMApp.tsx, before ThemeToggle). Inside
 * QuickSearchProvider it opens the palette directly; outside one it asks through
 * requestQuickSearch's window event, which the provider answers.
 */
import { Search } from 'lucide-react';
import { QUICK_WORDS } from './quickSearchLogic';
import { requestQuickSearch, useQuickSearch } from './QuickSearchPalette';
import { shortcutHintHere } from './useQuickSearchHotkey';

export interface QuickSearchTriggerProps {
  /** Margins or flex utilities only, by convention. */
  className?: string;
}

const TRIGGER =
  'inline-flex min-h-[44px] min-w-[44px] flex-shrink-0 items-center justify-center rounded-lg text-white/90 transition-colors duration-150 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/60 sm:h-8 sm:min-h-0 sm:min-w-0 sm:justify-start sm:gap-2 sm:bg-white/10 sm:px-2.5 sm:text-white/80 sm:hover:bg-white/15 md:w-48 2xl:w-64';

export default function QuickSearchTrigger({ className = '' }: QuickSearchTriggerProps) {
  const controls = useQuickSearch();
  const hint = shortcutHintHere();
  const label = `${QUICK_WORDS.trigger} (${hint})`;
  const open = () => {
    if (controls) controls.openQuickSearch();
    else requestQuickSearch();
  };
  return (
    <button
      type="button"
      onClick={open}
      aria-label={label}
      title={label}
      aria-haspopup="dialog"
      aria-expanded={controls ? controls.open : undefined}
      aria-keyshortcuts="Control+K Meta+K"
      data-quick-search-trigger=""
      className={`${TRIGGER} ${className}`}
    >
      <Search className="h-5 w-5 flex-shrink-0 sm:h-4 sm:w-4" aria-hidden="true" />
      <span className="hidden truncate whitespace-nowrap text-sm sm:inline">{QUICK_WORDS.trigger}</span>
      <kbd className="ml-auto hidden flex-shrink-0 rounded border border-white/25 px-1.5 font-sans text-[11px] font-medium leading-4 text-white/70 md:inline-block">
        {hint}
      </kbd>
    </button>
  );
}
